import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Container,
  Box,
  Paper,
  Typography,
  Stepper,
  Step,
  StepLabel,
  Snackbar,
  Alert,
  Button,
  CircularProgress,
  Chip,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import LockIcon from '@mui/icons-material/Lock';
import { getRankingDetail } from '../../services/ranking.service';
import StepBackup from './StepBackup';
import StepTorneos from './StepTorneos';
import StepCalculo from './StepCalculo';
import StepReportes from './StepReportes';

// En modo normal: 4 pasos. En modo solo lectura: se oculta el paso 0 (Resguardo),
// por lo que el stepper muestra solo los pasos 1, 2 y 3 reindexados.
const ALL_STEPS   = ['Resguardo', 'Torneos', 'Cálculo', 'Creación de Reportes'];
const RO_STEPS    = ['Torneos', 'Cálculo', 'Creación de Reportes'];

export default function RankingCreateWizard({ readOnly = false }) {
  const navigate = useNavigate();
  const { rkgIdParam } = useParams();
  const isEdit = !!rkgIdParam && !readOnly;

  // En modo solo lectura el wizard arranca en paso 0 del stepper visible,
  // que corresponde al paso 1 del wizard completo (Torneos).
  // En modo normal arranca en paso 0 (Resguardo).
  const [activeStep, setActiveStep] = useState(readOnly ? 0 : 0);
  const [rkgId, setRkgId] = useState(rkgIdParam ? Number(rkgIdParam) : null);
  const [dataLoading, setDataLoading] = useState(!!rkgIdParam);

  // Estado del paso Torneos
  const [fecha, setFecha] = useState('');
  const [torneosAsignados, setTorneosAsignados] = useState([]);

  const [alertInfo, setAlertInfo] = useState({ open: false, message: '', severity: 'info' });

  // Carga inicial cuando hay un ranking preexistente
  useEffect(() => {
    if (!rkgIdParam) return;
    const load = async () => {
      setDataLoading(true);
      try {
        const detail = await getRankingDetail(Number(rkgIdParam));
        setTorneosAsignados(detail.ranking?.torneosAsignados || []);
        if (detail.ranking?.fecha) {
          setFecha(detail.ranking.fecha);
        }
      } catch (e) {
        setAlertInfo({ open: true, message: 'Error cargando datos del ranking.', severity: 'error' });
      } finally {
        setDataLoading(false);
      }
    };
    load();
  }, [rkgIdParam]);

  if (dataLoading) {
    return (
      <Container maxWidth="lg" sx={{ pt: 8, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress />
      </Container>
    );
  }

  // Pasos visibles según el modo
  const steps = readOnly ? RO_STEPS : ALL_STEPS;

  // En modo solo lectura, el índice visible 0 → paso Torneos (real 1),
  // visible 1 → Cálculo (real 2), visible 2 → Reportes (real 3).
  // En modo normal el índice coincide con el paso real.
  const realStep = readOnly ? activeStep + 1 : activeStep;

  return (
    <Container maxWidth="lg" sx={{ pt: 1, pb: 4 }}>
      {/* Botón volver */}
      <Box sx={{ mb: 1 }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/ranking-management')}
          sx={{ color: '#94a3b8', borderRadius: 0, '&:hover': { color: '#cbd5e1' } }}
        >
          Volver a Gestión de Ranking
        </Button>
      </Box>

      {/* Título */}
      <Box sx={{ mb: 1.5, display: 'flex', alignItems: 'center', gap: 2 }}>
        <Typography variant="h4" component="h1" sx={{ color: '#cbd5e1', fontWeight: 'bold' }}>
          {readOnly
            ? 'Detalle de Ranking GRAU'
            : isEdit
            ? 'Editar Cálculo de Ranking GRAU'
            : 'Nuevo Cálculo de Ranking GRAU'}
        </Typography>
        {readOnly && (
          <Chip
            icon={<LockIcon sx={{ fontSize: '0.9rem !important' }} />}
            label="Solo lectura"
            size="small"
            sx={{
              bgcolor: 'rgba(100,116,139,0.15)',
              color: '#64748b',
              border: '1px solid rgba(100,116,139,0.3)',
              borderRadius: '4px',
              fontWeight: 600,
              fontSize: '0.72rem',
            }}
          />
        )}
      </Box>

      {/* Stepper */}
      <Box sx={{ width: '100%', mb: 3 }}>
        <Stepper activeStep={activeStep} alternativeLabel>
          {steps.map((label, index) => (
            <Step key={label} disabled={index > activeStep}>
              <StepLabel
                StepIconProps={{
                  sx: {
                    '&.Mui-active': { color: readOnly ? '#64748b' : '#3a6eb6' },
                    '&.Mui-completed': { color: '#10b981' },
                    '&.Mui-disabled': { color: '#334155' },
                  },
                }}
              >
                <Typography
                  variant="caption"
                  sx={{
                    color:
                      index === activeStep
                        ? readOnly ? '#94a3b8' : '#3a6eb6'
                        : index < activeStep
                        ? '#10b981'
                        : '#64748b',
                    fontWeight: index === activeStep ? 'bold' : 'normal',
                  }}
                >
                  {label}
                </Typography>
              </StepLabel>
            </Step>
          ))}
        </Stepper>
      </Box>

      {/* Contenido de cada paso */}
      <Paper
        sx={{
          p: 3,
          background: 'rgba(30, 41, 59, 0.7)',
          backdropFilter: 'blur(12px)',
          border: readOnly
            ? '1px solid rgba(100,116,139,0.2)'
            : '1px solid rgba(255, 255, 255, 0.08)',
          boxShadow: '0 8px 32px 0 rgba(0, 0, 0, 0.37)',
          borderRadius: 0,
          '& .MuiButton-root': { borderRadius: 0 },
          '& .MuiOutlinedInput-root': { borderRadius: 0 },
          '& .MuiAlert-root': { borderRadius: 0 },
        }}
      >
        {/* Paso Resguardo — solo en modo normal */}
        {!readOnly && realStep === 0 && (
          <StepBackup
            onNext={() => setActiveStep(1)}
            onSalir={() => navigate('/ranking-management')}
            setAlertInfo={setAlertInfo}
          />
        )}

        {/* Paso Torneos */}
        {realStep === 1 && (
          <StepTorneos
            readOnly={readOnly}
            isEdit={isEdit}
            rkgId={rkgId}
            fecha={fecha}
            setFecha={setFecha}
            torneosAsignados={torneosAsignados}
            setTorneosAsignados={setTorneosAsignados}
            onSaveSuccess={(newRkgId) => {
              if (!rkgId) setRkgId(newRkgId);
            }}
            onNext={() => setActiveStep(readOnly ? 1 : 2)}
            onSalir={() => navigate('/ranking-management')}
            setAlertInfo={setAlertInfo}
          />
        )}

        {/* Paso Cálculo */}
        {realStep === 2 && (
          <StepCalculo
            readOnly={readOnly}
            rkgId={rkgId}
            onBack={() => setActiveStep(readOnly ? 0 : 1)}
            onNext={() => setActiveStep(readOnly ? 2 : 3)}
          />
        )}

        {/* Paso Reportes */}
        {realStep === 3 && (
          <StepReportes
            rkgId={rkgId}
            onBack={() => setActiveStep(readOnly ? 1 : 2)}
            onSalir={() => navigate('/ranking-management')}
          />
        )}
      </Paper>

      {/* Toast */}
      <Snackbar
        open={alertInfo.open}
        autoHideDuration={5000}
        onClose={() => setAlertInfo((p) => ({ ...p, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setAlertInfo((p) => ({ ...p, open: false }))}
          severity={alertInfo.severity}
          variant="filled"
          sx={{ width: '100%', borderRadius: 0 }}
        >
          {alertInfo.message}
        </Alert>
      </Snackbar>
    </Container>
  );
}
